import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Check, UserPlus, AlertTriangle, Info, X } from 'lucide-react';
import { api } from '../../utils/api';

const ICON_MAP = {
  FLEET_INVITE: UserPlus,
  COMPLIANCE: AlertTriangle,
  GENERAL: Info,
};

const COLOR_MAP = {
  FLEET_INVITE: 'text-emerald-400',
  COMPLIANCE: 'text-amber-400',
  GENERAL: 'text-blue-400',
};

const NotificationBell = ({ onOpenFleetInvites }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await api('/inbox?limit=15');
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch {
      // Table may not exist yet
    }
  }, []);

  // Initial load + poll every 30s
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const markRead = async (notificationId) => {
    try {
      await api(`/inbox/${notificationId}/read`, { method: 'POST' });
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await api('/inbox/read-all', { method: 'POST' });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.is_read) markRead(notif.notification_id);
    if (notif.type === 'FLEET_INVITE' && onOpenFleetInvites) {
      setOpen(false);
      onOpenFleetInvites();
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-1 sm:p-1.5 text-slate-400 hover:text-white transition"
        data-testid="notification-bell"
      >
        <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-72 sm:w-80 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700">
            <span className="text-white text-sm font-semibold">Notifications</span>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  disabled={loading}
                  className="text-xs text-emerald-400 hover:text-emerald-300 transition"
                  data-testid="mark-all-read-btn"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white p-0.5">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-72 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map(notif => {
                const Icon = ICON_MAP[notif.type] || Info;
                const color = COLOR_MAP[notif.type] || 'text-blue-400';
                return (
                  <button
                    key={notif.notification_id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`w-full text-left flex items-start gap-2.5 px-3 py-2.5 hover:bg-slate-700/50 transition border-b border-slate-700/50 last:border-0 ${
                      !notif.is_read ? 'bg-slate-750/30' : ''
                    }`}
                    data-testid={`notification-item-${notif.notification_id}`}
                  >
                    <div className={`mt-0.5 flex-shrink-0 ${color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className={`text-xs font-medium truncate ${!notif.is_read ? 'text-white' : 'text-slate-300'}`}>
                          {notif.title}
                        </p>
                        {!notif.is_read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                        )}
                      </div>
                      {notif.message && (
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">{notif.message}</p>
                      )}
                      <p className="text-[10px] text-slate-500 mt-0.5">{formatTime(notif.created_at)}</p>
                    </div>
                    {!notif.is_read && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markRead(notif.notification_id); }}
                        className="mt-0.5 p-1 text-slate-500 hover:text-emerald-400 rounded transition flex-shrink-0"
                        title="Mark as read"
                      >
                        <Check className="w-3 h-3" />
                      </button>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
